// ============================================
// FAQ 编辑锁 Hook（基于 Supabase Realtime Presence）
// 一人编辑时，其他人看到提示并无法进入编辑器
// 支持：活跃心跳 + 超时自动释放
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';

export interface LockInfo {
  user_id: string;
  user_name: string;
  user_email: string;
  faq_id: string;
  locked_at: string;
}

interface EditingLockState {
  /** 当前所有锁 Map<faq_id, LockInfo> */
  locks: Map<string, LockInfo>;
  /** 检查某篇 FAQ 是否被锁（自动过滤已超时的锁） */
  checkLock: (faqId: string) => { locked: boolean; lockedBy?: string };
  /** 尝试抢锁（返回是否成功） */
  acquireLock: (faqId: string) => boolean;
  /** 释放当前用户持有的锁 */
  releaseLock: () => void;
  /** 当前用户是否正在编辑某篇 */
  myLockedFaqId: string | null;
  /** 锁即将过期（无操作 2 分钟）— 显示警告用 */
  lockExpiring: boolean;
  /** 用户点击“继续编辑”时调用，重置活跃计时 */
  refreshActivity: () => void;
}

const CHANNEL_NAME = 'faq-editing-locks';
/** 锁超时时间（毫秒）— 超过此时间未刷新心跳则视为过期 */
const LOCK_TIMEOUT_MS = 10 * 60 * 1000; // 10 分钟
/** 心跳间隔（毫秒）— 有活动时每隔此时间刷新一次锁 */
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000; // 2 分钟
/** 活跃检测窗口 — 在此时间内有操作才算"活跃" */
const ACTIVITY_WINDOW_MS = 3 * 60 * 1000; // 3 分钟

export function useEditingLock(): EditingLockState {
  const user = useAuthStore((s) => s.user);
  const profile = useUserStore((s) => s.profile);
  const [locks, setLocks] = useState<Map<string, LockInfo>>(new Map());
  const [myLockedFaqId, setMyLockedFaqId] = useState<string | null>(null);
  const [lockExpiring, setLockExpiring] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 获取当前用户显示名
  const getUserName = useCallback(() => {
    if (profile?.name) return profile.name;
    if (user?.email) return user.email.split('@')[0];
    return '未知用户';
  }, [profile, user]);

  // 判断锁是否过期
  const isLockExpired = useCallback((lockedAt: string): boolean => {
    const lockTime = new Date(lockedAt).getTime();
    return Date.now() - lockTime > LOCK_TIMEOUT_MS;
  }, []);

  // 初始化 Presence channel
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const newLocks = new Map<string, LockInfo>();

      for (const [, presences] of Object.entries(state)) {
        for (const p of presences as any[]) {
          if (p.faq_id) {
            newLocks.set(p.faq_id, {
              user_id: p.user_id,
              user_name: p.user_name,
              user_email: p.user_email || '',
              faq_id: p.faq_id,
              locked_at: p.locked_at,
            });
          }
        }
      }

      setLocks(newLocks);
    });

    channel.subscribe();

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [user]);

  // ── 活跃检测：监听用户操作，刷新 lastActivity ──
  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => document.addEventListener(e, markActive, { passive: true }));
    return () => {
      events.forEach((e) => document.removeEventListener(e, markActive));
    };
  }, []);

  // ── 心跳：持有锁时，定期刷新 locked_at（仅在活跃状态下）──
  useEffect(() => {
    if (!myLockedFaqId) {
      // 没有锁，清除心跳
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    heartbeatRef.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;

      if (timeSinceActivity > ACTIVITY_WINDOW_MS) {
        // 用户长时间无操作 → 自动释放锁
        if (channelRef.current) {
          channelRef.current.untrack();
        }
        setMyLockedFaqId(null);
        setLockExpiring(false);
        return;
      }

      // 2 分钟无操作 → 预警（不刷新心跳，让锁自然过期）
      if (timeSinceActivity > HEARTBEAT_INTERVAL_MS) {
        setLockExpiring(true);
        return; // 不刷新心跳！
      }

      // 用户活跃 → 清除预警 + 刷新心跳
      setLockExpiring(false);
      if (channelRef.current && user) {
        channelRef.current.track({
          user_id: user.id,
          user_name: getUserName(),
          user_email: user.email || '',
          faq_id: myLockedFaqId,
          locked_at: new Date().toISOString(),
        });
      }
    }, HEARTBEAT_INTERVAL_MS / 2); // 每 1 分钟检查一次

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [myLockedFaqId, user, getUserName]);

  // 检查某篇 FAQ 是否被锁（自动过滤过期锁）
  const checkLock = useCallback(
    (faqId: string): { locked: boolean; lockedBy?: string } => {
      const lock = locks.get(faqId);
      if (!lock) return { locked: false };
      // 自己持有的锁不算被锁
      if (lock.user_id === user?.id) return { locked: false };
      // 检查是否已过期
      if (isLockExpired(lock.locked_at)) return { locked: false };
      // 拼接显示：姓名（email）
      const display = lock.user_name && lock.user_email
        ? `${lock.user_name}（${lock.user_email}）`
        : lock.user_email || lock.user_name;
      return { locked: true, lockedBy: display };
    },
    [locks, user, isLockExpired],
  );

  // 抢锁
  const acquireLock = useCallback(
    (faqId: string): boolean => {
      if (!channelRef.current || !user) return false;

      // 检查是否已被他人锁定（未过期的锁）
      const existing = locks.get(faqId);
      if (existing && existing.user_id !== user.id && !isLockExpired(existing.locked_at)) {
        return false;
      }

      // 重置活跃时间
      lastActivityRef.current = Date.now();

      channelRef.current.track({
        user_id: user.id,
        user_name: getUserName(),
        user_email: user.email || '',
        faq_id: faqId,
        locked_at: new Date().toISOString(),
      });

      setMyLockedFaqId(faqId);
      return true;
    },
    [locks, user, getUserName, isLockExpired],
  );

  // 用户点击“继续编辑”时调用
  const refreshActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setLockExpiring(false);
    // 立即刷新心跳
    if (channelRef.current && user && myLockedFaqId) {
      channelRef.current.track({
        user_id: user.id,
        user_name: getUserName(),
        user_email: user.email || '',
        faq_id: myLockedFaqId,
        locked_at: new Date().toISOString(),
      });
    }
  }, [user, myLockedFaqId, getUserName]);

  // 释放锁
  const releaseLock = useCallback(() => {
    if (channelRef.current) {
      channelRef.current.untrack();
    }
    setMyLockedFaqId(null);
    setLockExpiring(false);
  }, []);

  // 页面关闭前释放锁
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (channelRef.current) {
        channelRef.current.untrack();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return { locks, checkLock, acquireLock, releaseLock, myLockedFaqId, lockExpiring, refreshActivity };
}
